import React from 'react';
import { Loader2 } from 'lucide-react';

interface QuizLoadingProps {
  type?: 'dashboard' | 'workspace' | 'spinner';
  message?: string;
}

export const QuizLoading: React.FC<QuizLoadingProps> = ({
  type = 'workspace',
  message = 'Đang tải dữ liệu đánh giá...',
}) => {
  if (type === 'spinner') {
    return (
      <div className="fc-loading-spinner-box">
        <div className="fc-loading-spinner-rings">
          <Loader2 className="fc-spinner-rotate" size={36} />
        </div>
        <p className="fc-loading-text">{message}</p>
      </div>
    );
  }

  if (type === 'dashboard') {
    return (
      <div className="fc-dashboard-container fc-loading-fadein">
        {/* Top Header Skeleton */}
        <div className="fc-dash-header">
          <div>
            <div className="fc-shimmer fc-sk-text" style={{ width: '130px', height: '14px', marginBottom: '8px' }} />
            <div className="fc-shimmer fc-sk-text" style={{ width: '260px', height: '28px' }} />
          </div>
          <div className="fc-shimmer fc-sk-pill" style={{ width: '90px', height: '26px' }} />
        </div>

        {/* Hero Progress Banner Skeleton */}
        <div className="fc-dash-hero fc-sk-hero">
          <div className="fc-dash-hero-info" style={{ width: '100%' }}>
            <div className="fc-shimmer fc-sk-text" style={{ width: '80px', height: '12px', marginBottom: '10px' }} />
            <div className="fc-shimmer fc-sk-text" style={{ width: '150px', height: '24px', marginBottom: '16px' }} />
            <div className="fc-shimmer fc-sk-bar" style={{ width: '100%', height: '8px' }} />
          </div>
          <div className="fc-shimmer fc-sk-btn" style={{ width: '180px', height: '42px', flexShrink: 0 }} />
        </div>

        {/* Days Grid Skeleton */}
        <div className="fc-dash-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((dayNum) => (
            <div key={dayNum} className="fc-dash-day-card fc-sk-card">
              <div className="fc-dash-day-top">
                <div style={{ flex: 1 }}>
                  <div className="fc-shimmer fc-sk-text" style={{ width: '60px', height: '14px', marginBottom: '6px' }} />
                  <div className="fc-shimmer fc-sk-text" style={{ width: '140px', height: '16px' }} />
                </div>
                <div className="fc-shimmer fc-sk-pill" style={{ width: '80px', height: '22px' }} />
              </div>

              <div className="fc-dash-day-progress" style={{ marginTop: '20px' }}>
                <div className="fc-shimmer fc-sk-bar" style={{ width: '100%', height: '6px' }} />
                <div className="fc-dash-day-meta" style={{ marginTop: '8px' }}>
                  <div className="fc-shimmer fc-sk-text" style={{ width: '45px', height: '12px' }} />
                  <div className="fc-shimmer fc-sk-text" style={{ width: '80px', height: '12px' }} />
                </div>
              </div>

              <div className="fc-shimmer fc-sk-btn" style={{ width: '100%', height: '38px', marginTop: '18px' }} />
            </div>
          ))}
        </div>

        <div className="fc-loading-overlay-banner">
          <Loader2 className="fc-spinner-rotate" size={20} />
          <span>{message}</span>
        </div>
      </div>
    );
  }

  // Workspace Skeleton (Side bar + main question card)
  return (
    <div className="fc-loading-workspace fc-loading-fadein">
      {/* Subbar Skeleton */}
      <div className="fc-quiz-subbar fc-sk-subbar">
        <div className="fc-subbar-title-group">
          <div className="fc-shimmer fc-sk-btn" style={{ width: '100px', height: '32px' }} />
          <div className="fc-shimmer fc-sk-pill" style={{ width: '70px', height: '26px' }} />
          <div className="fc-shimmer fc-sk-text" style={{ width: '180px', height: '18px' }} />
        </div>
        <div className="fc-subbar-controls">
          <div className="fc-shimmer fc-sk-pill" style={{ width: '180px', height: '32px' }} />
          <div className="fc-shimmer fc-sk-pill" style={{ width: '100px', height: '32px' }} />
        </div>
      </div>

      <div className="fc-workspace">
        {/* Sidebar Skeleton */}
        <aside className="fc-sidebar fc-sk-sidebar">
          <div className="fc-sidebar-header">
            <div>
              <div className="fc-shimmer fc-sk-text" style={{ width: '70px', height: '14px', marginBottom: '4px' }} />
              <div className="fc-shimmer fc-sk-text" style={{ width: '90px', height: '12px' }} />
            </div>
          </div>

          <div className="fc-grid-container" style={{ marginTop: '12px' }}>
            {Array.from({ length: 34 }).map((_, idx) => (
              <div key={idx} className="fc-shimmer fc-sk-grid-dot" />
            ))}
          </div>

          <div className="fc-stats-panel" style={{ marginTop: '20px' }}>
            <div className="fc-stat-col">
              <div className="fc-shimmer fc-sk-text" style={{ width: '24px', height: '18px', margin: '0 auto 4px' }} />
              <div className="fc-shimmer fc-sk-text" style={{ width: '48px', height: '10px', margin: '0 auto' }} />
            </div>
            <div className="fc-stat-col">
              <div className="fc-shimmer fc-sk-text" style={{ width: '24px', height: '18px', margin: '0 auto 4px' }} />
              <div className="fc-shimmer fc-sk-text" style={{ width: '48px', height: '10px', margin: '0 auto' }} />
            </div>
            <div className="fc-stat-col">
              <div className="fc-shimmer fc-sk-text" style={{ width: '24px', height: '18px', margin: '0 auto 4px' }} />
              <div className="fc-shimmer fc-sk-text" style={{ width: '48px', height: '10px', margin: '0 auto' }} />
            </div>
          </div>
        </aside>

        {/* Content Area Skeleton */}
        <main className="fc-content-area">
          <div className="fc-content-wrapper">
            <div className="fc-question-card fc-sk-qcard">
              <div className="fc-loading-center-badge">
                <Loader2 className="fc-spinner-rotate" size={24} />
                <span>{message}</span>
              </div>

              <div className="fc-qcard-top" style={{ marginBottom: '18px' }}>
                <div className="fc-shimmer fc-sk-pill" style={{ width: '70px', height: '24px' }} />
                <div className="fc-shimmer fc-sk-pill" style={{ width: '160px', height: '24px', marginLeft: '10px' }} />
              </div>

              <div className="fc-shimmer fc-sk-text" style={{ width: '90%', height: '22px', marginBottom: '10px' }} />
              <div className="fc-shimmer fc-sk-text" style={{ width: '65%', height: '22px', marginBottom: '24px' }} />

              <div className="fc-options-list">
                {[1, 2, 3, 4, 5].map((optNum) => (
                  <div key={optNum} className="fc-shimmer fc-sk-option" />
                ))}
              </div>
            </div>

            {/* Desktop Bottom Nav Skeleton */}
            <div className="fc-desktop-nav fc-sk-nav" style={{ marginTop: '16px' }}>
              <div className="fc-shimmer fc-sk-btn" style={{ width: '100px', height: '38px' }} />
              <div className="fc-shimmer fc-sk-text" style={{ width: '140px', height: '16px' }} />
              <div className="fc-shimmer fc-sk-btn" style={{ width: '100px', height: '38px' }} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default QuizLoading;
